import { Link } from "react-router-dom";
import { ArrowRight, Truck, Wind, ShieldAlert, Layers, Cog, PackageSearch, Snowflake, Plane } from "lucide-react";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { FaqSection } from "./Faq";
import { Reveal } from "./anim";
import { FLEET, FLEET_GALLERY, FLEET_INDEX_FAQ, IMAGES, SINCE, breadcrumb, faqSchema, SITE } from "./marketingData";

const ICONS = { Wind, ShieldAlert, Layers, Cog, Truck, PackageSearch, Snowflake };

export const AerospaceBanner = ({ id }) => (
  <Link to="/aerospace" data-testid={`aerospace-banner-${id}`}
    className="group mt-12 flex flex-col sm:flex-row sm:items-center gap-6 rounded-2xl bg-[#0E1116] text-white overflow-hidden hover:shadow-xl transition-shadow">
    <img src={IMAGES.aeroEngine} alt="Aero engine on an air-suspension trailer" className="h-44 sm:h-36 sm:w-64 w-full object-cover" />
    <div className="px-6 pb-6 sm:p-0 sm:pr-8 flex-1">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-1 inline-flex items-center gap-2"><Plane className="w-4 h-4" /> Aerospace</p>
      <h2 className="font-heading font-bold text-xl mb-1">Aero engine transport for engine makers & MROs</h2>
      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#C84B31]">See how we move engines <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" /></span>
    </div>
  </Link>
);

export default function FleetIndex() {
  return (
    <div className="bg-[#F9F8F6] text-[#1A1A18] font-body antialiased overflow-x-hidden" data-testid="fleet-index">
      <Seo
        title={`Our Fleet — Trailers, Trucks & Heavy-Haul Vehicles | ITO`}
        description="Explore Indian Transport Organisation's 800+ owned fleet — air-suspension trailers, licensed explosive vans, low-bed, semi low-bed, high-bed and modular heavy-haul trailers, reefer trucks for defence and chemical cargo, trucks and city tempos."
        path="/fleet"
        keywords="fleet of trailers india, trailer transporter india, air suspension trailer, explosive van, low bed trailer, semi low bed trailer, modular heavy haul trailer, reefer truck defence chemicals, aero engine transport, transport trucks india"
        jsonLd={[
          breadcrumb([{ name: "Home", path: "/" }, { name: "Fleet", path: "/fleet" }]),
          {
            "@context": "https://schema.org", "@type": "ItemList",
            itemListElement: FLEET.map((f, i) => ({
              "@type": "ListItem", position: i + 1, name: f.name,
              url: `${SITE}/fleet/${f.slug}`,
            })),
          },
          faqSchema(FLEET_INDEX_FAQ),
        ]}
      />
      <MarketingNav heroDark />

      <section className="relative pt-36 pb-16 bg-[#1A1A18] text-white overflow-hidden">
        <img src={IMAGES.fleetYard} alt="ITO fleet yard" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8">
          <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3">Our own fleet · since {SINCE}</p>
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.03] max-w-3xl">800+ owned vehicles — pick the one your cargo needs.</h1>
          <p className="mt-5 text-white/80 max-w-2xl leading-relaxed">From air-suspension trailers for sensitive machinery to licensed explosive vans and modular heavy-haul rigs — tap any vehicle for full details and a shareable link.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FLEET.map((f, i) => {
            const Icon = ICONS[f.icon] || Truck;
            return (
              <Reveal key={f.slug} delay={(i % 3) * 0.06}>
                <Link to={`/fleet/${f.slug}`} data-testid={`fleet-index-card-${f.slug}`}
                  className="group block h-full rounded-2xl bg-white border border-[#1A1A18]/10 overflow-hidden hover:border-[#C84B31]/50 hover:shadow-xl transition-all hover:-translate-y-1">
                  <div className="relative h-44 overflow-hidden">
                    <img src={(FLEET_GALLERY[f.slug] && FLEET_GALLERY[f.slug][0]) || IMAGES[f.img] || IMAGES.fleetYard} alt={f.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/60 to-transparent" />
                    <span className="absolute top-4 left-4 inline-flex w-9 h-9 rounded-lg items-center justify-center bg-[#C84B31]"><Icon className="w-4 h-4 text-white" /></span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-semibold text-lg mb-2">{f.name}</h3>
                    <p className="text-sm text-[#4F4C44] leading-relaxed line-clamp-3">{f.desc}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C84B31]">View details <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" /></span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8"><AerospaceBanner id="fleet" /></div>
      </section>

      <FaqSection faqs={FLEET_INDEX_FAQ} title="Fleet — frequently asked questions" testid="fleet-index-faq" />

      <MarketingFooter />
    </div>
  );
}
